// Import custom transition functions from the Timedoor module.
import { slideUp, slideDown, slideToggle, fadeOut, fadeIn, fadeToggle } from './modules/transitions';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

// Import specific Bootstrap components for modular use in the project.
import { Offcanvas, Modal, Tab } from 'bootstrap';

/**
 * Coding Standards
 * ===========================
 * 1. Functionality Splitting:
 *    - Create distinct, single-purpose functions to improve readability and reusability.
 * 2. Modular Imports:
 *    - Import only the required modules to reduce bundle size and enhance maintainability.
 * 3. Event Listeners:
 *    - Always wrap initialization code inside `DOMContentLoaded` to ensure the DOM is fully loaded.
 */

function checkNavbarScrolled() {
    const navbarPrimaryEl = document.querySelector('.custom-navbar')

    if (navbarPrimaryEl) {
        window.addEventListener('scroll', () => {
            if (window.scrollY > 50) {
                navbarPrimaryEl.classList.add('scrolled')
            } else {
                navbarPrimaryEl.classList.remove('scrolled')
            }
        })
    }
}

function navbarMegaMenu() {
    // use jquery for better result
    (function($) {
        const $menus = $('.menu-item-has-children, .menu-item-mega-menu');
        let activeMenu = null;
        let closeTimeout = null;

        function closeMenu($menu) {
            $menu.find('.sub-menu, .mega-menu')
                .stop(true, true).slideUp(200);
            $menu.removeClass('is-open');
        }

        function openMenu($menu) {
            $menu.find('.sub-menu, .mega-menu')
                .stop(true, true).slideDown(200);
            $menu.addClass('is-open');
        }

        $menus.on('mouseenter', function () {
            clearTimeout(closeTimeout);

            const $menu = $(this);

            if (activeMenu && activeMenu[0] !== $menu[0]) {
                closeMenu(activeMenu);
            }

            openMenu($menu);
            activeMenu = $menu;
        });

        $menus.on('mouseleave', function () {
            const $menu = $(this);
            clearTimeout(closeTimeout);
            closeTimeout = setTimeout(() => {
                if (activeMenu && activeMenu[0] === $menu[0]) {
                    closeMenu($menu);
                    activeMenu = null;
                }
            }, 200);
        });

        $(document).on('click', function (e) {
            if (!$menus.is(e.target) && $menus.has(e.target).length === 0) {
                if (activeMenu) {
                    closeMenu(activeMenu);
                    activeMenu = null;
                }
            }
        });
    })(jQuery);
}

function imageAnimation() {
    const imageElements = document.querySelectorAll('.image-transition');
    imageElements.forEach(image => {
        gsap.to(image, {
            ease: "power2.out",
            delay: 0.4,
            duration: 0.8,
            opacity: 1,
            scale: 1,
            clipPath: "inset(0% 0% 0% 0%)",
            scrollTrigger: {
                trigger: image,
                start: 'top 100%',
            }
        })
    });
}

function sliderightAnimation() {
    const imageElements = document.querySelectorAll('.anim-slide-right');
    imageElements.forEach(image => {
        ScrollTrigger.create({
            trigger: image,
            start: 'top 90%',
            onEnter: () => {
                image.classList.add('on');
            },
        });
    });
}

function fadeinAnimation() {
    const imageElements = document.querySelectorAll('.anim-fade-in');
    imageElements.forEach(image => {
        ScrollTrigger.create({
            trigger: image,
            start: 'top 80%',
            onEnter: () => {
                image.classList.add('on');
            },
        });
    });
}

function runningTextAnimation() {
    const runningText = document.querySelectorAll('.running-text__swiper');

    if (!runningText) return;

    runningText.forEach(rt => {
        const swiper = new Swiper(rt, {
            modules: [Autoplay],
            slidesPerView: 'auto',
            loop: true,
            spaceBetween: 0,
            autoplay: {
                delay: 0,
                disableOnInteraction: false,
            },
            speed: 20000,
            allowTouchMove: false,
        });
    });
}

function paralaxImageAnimation() {
    const imageElements = document.querySelectorAll('.anim-paralax-image-transition');

    imageElements.forEach(image => {
        gsap.to(image, {
            ease: "power2.out",
            delay: 0.4,
            duration: 0.8,
            opacity: 1,
            scale: 1.4,
            clipPath: "inset(0% 0% 0% 0%)",
            scrollTrigger: {
                trigger: image,
                start: 'top 100%',
            }
        })

        gsap.fromTo(image, {
            y: '20%'
        }, {
            y: '-20%',
            ease: 'none',
            scrollTrigger: {
                trigger: image.parentElement,
                start: 'top 80%',
                end: 'bottom 40%',
                scrub: 0.5,
                // markers: true, // Uncomment for debugging scroll trigger start/end points
            }
        });
        
    });
}

function pageHeaderAnimation() {
    // paralax animation

    const pageHeader = document.querySelector('.page-header__image-container')

    if (!pageHeader) return;

    const tl = gsap.timeline()

    tl.to('.page-header__image-container', {
        opacity: 1,
        delay: 0.5,
        duration: 0.6,
    })
    .to('.page-header__image-container img', {
        duration: 0.6,
        scale: 1.025,
    }, '<')
    .to('.page-header__image-container img', {
        y: '50%',
        ease: 'none',
        scrollTrigger: {
            trigger: '.page-header__image-container img',
            start: 'top top',
            scrub: true,
            // markers: true, // Uncomment for debugging scroll trigger start/end points
        }
    });
}

function resetScrollTrigger() {
    document.addEventListener('shown.bs.tab', event => {
        ScrollTrigger.refresh()
    })
}

function populateSidenavFromSections() {
    const sectionNavLabel = document.querySelectorAll('[data-nav-label]');

    if (sectionNavLabel.length === 0) {
        console.warn("No 'data-nav-label' elements or '.sidenav__item' elements found to populate.");
        return;
    }

    // Create the main <nav> element for the sidebar.
    const sidenav = document.createElement('nav');
    sidenav.classList.add('sidenav');

    // Create the <ul> element for the list of navigation items.
    const sidenavList = document.createElement('ul');
    sidenavList.classList.add('sidenav__list');

    // Loop through the found 'data-nav-label' elements to create list items.
    sectionNavLabel.forEach(section => {
        const navLabel = section.dataset.navLabel; // Get the value of data-href
        const sectionTarget = section.dataset.href; // Get the value of data-href

        // Only create an item if the sectionTarget exists (is not empty or null)
        if (sectionTarget) {
            // Create the <li> element for each navigation item.
            const sidenavItem = document.createElement('li');
            sidenavItem.classList.add('sidenav__item');

            // Create the <a> element for the link.
            const sidenavLink = document.createElement('a');
            const icon = '<svg width="19" height="17" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_4193_226)"><path d="M16.8129 0.715088L0 17.0007H3.87708L16.8129 4.52583V0.715088Z" fill="currentColor"></path><path d="M17 9.00073L9 17.0007H11.7051L17 11.7296V9.00073Z" fill="currentColor"></path></g><defs><clipPath id="clip0_4193_226"><rect width="19" height="17" fill="white" transform="translate(0 0.000732422)"></rect></clipPath></defs></svg>'
            sidenavLink.classList.add('sidenav__link');
            sidenavLink.innerHTML = icon + ' ' + navLabel; // Set the text of the link

            // Set the href attribute.
            sidenavLink.href = `#${sectionTarget.toLowerCase().replace(/\s/g, '-')}`;

            // Append the link to the list item.
            sidenavItem.appendChild(sidenavLink);

            // Append the list item to the unordered list.
            sidenavList.appendChild(sidenavItem);

            // console.log(`Created sidenav item for: ${sectionName}`);
        }
    });

    // Append the <ul> to the <nav>.
    sidenav.appendChild(sidenavList);

    // Append the entire <nav> to the document body.
    document.body.appendChild(sidenav);
}

function scrollToSectionDelay() {
    let hash = window.location.hash; // This will be URL-encoded, e.g., "#%E3%83%81%E3%83%BC%E3%83%A0%E7%B4%B9%E4%BB%8B"

    if (hash) {
        // Decode the hash to get the actual ID string (e.g., "#チーム紹介")
        hash = decodeURIComponent(hash);

        // Temporarily remove the hash from the URL to prevent immediate browser scroll
        if (history.replaceState) {
            history.replaceState(null, null, window.location.pathname + window.location.search);
        } else {
            window.location.hash = ''; // Fallback
        }

        const delay = 1000; // 1 second delay

        setTimeout(() => {
            // Use the decoded hash to select the element
            const targetElement = document.querySelector(hash);

            if (targetElement) {
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                // Re-add the original (encoded) hash to the URL after scrolling
                // For this, you might want to store the original encoded hash
                // or re-encode the decoded one. Re-encoding is safer.
                if (history.replaceState) {
                    history.replaceState(null, null, window.location.pathname + window.location.search + encodeURIComponent(hash));
                }
            }
        }, delay);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    checkNavbarScrolled()
    navbarMegaMenu()
    imageAnimation()
    sliderightAnimation()
    fadeinAnimation()
    paralaxImageAnimation()
    pageHeaderAnimation()
    runningTextAnimation()
    resetScrollTrigger()
    populateSidenavFromSections()
    scrollToSectionDelay()
});
